import { useState, useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useMutation } from '@apollo/client'
import message from '@/libs/message'
import policyDetailAtom from '@/recoil/policyDetail'
import { userGql } from '@/gql'
import userAtom from '@/recoil/user'
import { Form } from 'antd'
import { useTempPasswordStatus } from '@/services/tempPassword/useTempPasswordStatus'
import { useTempPassword } from '@/services/tempPassword/useTempPassword'

export enum EditModeEnum {
  EMAIL = 'EMAIL',
  MOBILE_NUMBER = 'MOBILE_NUMBER',
  DRIVER_MOBILE_NUMBER = 'DRIVER_MOBILE_NUMBER',
}

function CustomerProfileHook() {
  const [form] = Form.useForm<{
    currentEmail: string
    email: string
    currentMobileNumber: string
    mobileNumber: string
    currentDriverMobileNumber: string
    driverMobileNumber: string
  }>()
  const user = useRecoilValue(userAtom)
  const [policyDetail, setPolicyDetail] = useRecoilState(policyDetailAtom)
  const [backofficeUpdateUser, { error: backofficeUpdateUserError }] = useMutation(
    userGql.mutation.backofficeUpdateUser,
  )
  const [editMode, setEditMode] = useState<EditModeEnum | undefined>(undefined)
  const [disableSave, setDisableSave] = useState(true)
  const [canViewTempPassword, setCanViewTempPassword] = useState(false)
  const [openTempPasswordModal, setOpenTempPasswordModal] = useState(false)
  const [tempPassword, setTempPassword] = useState('')
  const [expiredAt, setExpiredAt] = useState<Date | null>(null)
  const fetchTempPasswordStatus = useTempPasswordStatus({ userId: policyDetail.id ?? '' })
  const fetchTempPassword = useTempPassword({
    userId: policyDetail.id ?? '',
  })
  const modalProperty = {
    [EditModeEnum.EMAIL]: {
      title: 'คุณกำลังแก้ไขอีเมลลูกค้า',
      subtitle: 'กรุณาตรวจสอบ email ว่าสะกดถูกต้อง',
      dataTestId: 'policy-detail-edit-email-modal',
      form: {
        currentValueInput: {
          label: 'อีเมลปัจจุบัน',
          name: 'currentEmail',
          dataTestId: 'policy-detail-current-email-input',
        },
        newValueInput: {
          label: 'อีเมลใหม่',
          name: 'email',
          rule: [{ type: 'email', required: true, message: 'ไม่ได้อยู่ในรูปแบบอีเมล' }],
          placeholder: 'ระบุอีเมลใหม่',
          dataTestId: 'policy-detail-edit-email-input',
          type: 'email',
        },
      },
    },
    [EditModeEnum.MOBILE_NUMBER]: {
      title: 'คุณกำลังแก้ไขเบอร์โทรศัพท์ลูกค้า',
      subtitle: 'กรุณาทวนตัวเลขเบอร์โทรศัพท์กับลูกค้า หรือตรวจสอบเลขที่คุณกรอก',
      dataTestId: 'policy-detail-edit-mobileNumber-modal',
      form: {
        currentValueInput: {
          label: 'เบอร์โทรศัพท์เดิม',
          name: 'currentMobileNumber',
          dataTestId: 'policy-detail-current-mobileNumber-input',
        },
        newValueInput: {
          label: 'เบอร์โทรศัพท์',
          name: 'mobileNumber',
          rule: [
            {
              required: true,
              pattern: /^[0]\d{9}$/g,
              message: 'ข้อมูลไม่ถูกต้อง',
            },
          ],
          placeholder: 'ระบุเบอร์โทรศัพท์ใหม่',
          dataTestId: 'policy-detail-edit-mobileNumber-input',
          type: 'tel',
        },
      },
    },
    [EditModeEnum.DRIVER_MOBILE_NUMBER]: {
      title: 'คุณกำลังแก้ไขเบอร์โทรศัพท์ driver',
      subtitle: 'กรุณาทวนตัวเลขเบอร์โทรศัพท์กับลูกค้า หรือตรวจสอบเลขที่คุณกรอก',
      dataTestId: 'policy-detail-edit-driver-mobileNumber-modal',
      form: {
        currentValueInput: {
          label: 'เบอร์โทรศัพท์ driver เดิม',
          name: 'currentDriverMobileNumber',
          dataTestId: 'policy-detail-current-driver-mobileNumber-input',
        },
        newValueInput: {
          label: 'เบอร์โทรศัพท์ driver',
          name: 'driverMobileNumber',
          rule: [
            {
              required: true,
              pattern: /^[0]\d{9}$/g,
              message: 'ข้อมูลไม่ถูกต้อง',
            },
          ],
          placeholder: 'ระบุเบอร์โทรศัพท์ใหม่',
          dataTestId: 'policy-detail-edit-driver-mobileNumber-input',
          type: 'tel',
        },
      },
    },
  }

  useEffect(() => {
    if (backofficeUpdateUserError) {
      const errorCode = backofficeUpdateUserError.message

      switch (errorCode) {
        case 'BIZUSR1001':
          message.error('ไม่พบข้อมูลลูกค้า')
          break
        case 'BIZUSR1002':
          message.error('อีเมลนี้ถูกใช้ไปแล้ว')
          break
        case 'BIZUSR1003':
          message.error('เบอร์โทรศัพท์นี้ถูกใช้ไปแล้ว')
          break
        default:
          message.error('แก้ไขข้อมูลลูกค้าผิดพลาด')
      }
    }
  }, [backofficeUpdateUserError])

  const updateUser = async () => {
    const updateUser = form.getFieldsValue()

    // Driver mobile edit — targets the DRIVER account (driverAccountId), not the
    // displayed owner. Patch recoil in place (matches the owner edit UX — no full
    // re-search/reload). The mutation response is the DRIVER user, so read ONLY its
    // mobileNumber; never spread it over the displayed owner fields. Every listed
    // policy is bound to this same driver, so patch their driverAccountMobileNumber
    // too. Trade-off: the URL keeps the old driver mobile, so a manual browser
    // refresh would re-search the stale number — the same limitation the owner
    // edit-by-mobile already has; accepted here for the smoother no-reload UX.
    if (editMode === EditModeEnum.DRIVER_MOBILE_NUMBER) {
      if (updateUser.currentDriverMobileNumber === updateUser.driverMobileNumber) {
        return message.error('เบอร์โทรศัพท์นี้ถูกใช้ไปแล้ว')
      }

      try {
        if (policyDetail.driverAccountId) {
          const { data } = await backofficeUpdateUser({
            variables: {
              input: {
                userId: policyDetail.driverAccountId,
                mobileNumber: updateUser.driverMobileNumber,
              },
            },
          })

          if (data && data.backofficeUpdateUser) {
            const newDriverMobileNumber = data.backofficeUpdateUser.mobileNumber

            setPolicyDetail({
              ...policyDetail,
              driverAccountMobileNumber: newDriverMobileNumber,
              policies: policyDetail.policies.map((policy) => ({
                ...policy,
                driverAccountMobileNumber: newDriverMobileNumber,
              })),
            })

            message.success('แก้ไขเบอร์โทรศัพท์ driver สำเร็จ')
            form.resetFields()
            setEditMode(undefined)
          }
        }
      } catch (err) {
        console.error(err)
      }
      return
    }

    if (editMode === EditModeEnum.EMAIL && updateUser.currentEmail === updateUser.email) {
      return message.error('อีเมลนี้ถูกใช้ไปแล้ว')
    }

    if (
      editMode === EditModeEnum.MOBILE_NUMBER &&
      updateUser.currentMobileNumber === updateUser.mobileNumber
    ) {
      return message.error('เบอร์โทรศัพท์นี้ถูกใช้ไปแล้ว')
    }

    try {
      if (policyDetail.id) {
        const { data } = await backofficeUpdateUser({
          variables: {
            input: {
              userId: policyDetail.id,
              email: updateUser.email || undefined,
              mobileNumber: updateUser.mobileNumber || undefined,
            },
          },
        })

        if (data && data.backofficeUpdateUser) {
          const backofficeUpdateUserData = data.backofficeUpdateUser

          if (editMode === EditModeEnum.MOBILE_NUMBER) {
            setPolicyDetail({
              ...policyDetail,
              mobileNumber: backofficeUpdateUserData.mobileNumber,
            })

            message.success('แก้ไขเบอร์โทรศัพท์ลูกค้าสำเร็จ')
            form.resetFields()
          }

          if (editMode === EditModeEnum.EMAIL) {
            setPolicyDetail({
              ...policyDetail,
              email: backofficeUpdateUserData.email,
            })

            message.success('แก้ไขอีเมลลูกค้าสำเร็จ')
            form.resetFields()
          }

          setEditMode(undefined)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)
    setDisableSave(hasErrors)
  }

  const openModal = (editMode: EditModeEnum) => {
    setEditMode(editMode)
    form.resetFields()
  }

  const closeModal = () => {
    setEditMode(undefined)
    form.resetFields()
  }

  const toggleTempPasswordModal = () => {
    if (tempPassword && expiredAt) {
      // check if temp password is expired
      fetchTempPassword.refetch().then((res) => {
        if (res.isError) {
          message.warning('ไม่พบรหัสผ่านชั่วคราว หรือหมดอายุแล้ว')
          setCanViewTempPassword(false)
          onSetTempPassword('', null)
          closeTempPasswordModal()
          return
        }
      })
    }

    setOpenTempPasswordModal(!openTempPasswordModal)
  }

  const onSetTempPassword = (tempPassword: string, expiredAt: Date | null) => {
    setTempPassword(tempPassword)
    setExpiredAt(expiredAt)
  }

  const closeTempPasswordModal = () => {
    setOpenTempPasswordModal(false)
  }

  useEffect(() => {
    if (!canViewTempPassword) {
      onSetTempPassword('', null)
    }
  }, [canViewTempPassword])

  return {
    policyDetail,
    updateUser,
    user,
    editMode,
    modalProperty,
    openModal,
    closeModal,
    form,
    onFormChange,
    disableSave,
    canViewTempPassword,
    setCanViewTempPassword,
    fetchTempPasswordStatus,
    openTempPasswordModal,
    toggleTempPasswordModal,
    fetchTempPassword,
    onSetTempPassword,
    tempPassword,
    expiredAt,
    closeTempPasswordModal,
  }
}

export default CustomerProfileHook
